import {useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import type {AnalyticsConstant} from 'nutshell-core/nut-analytics-client';
import type {User} from 'nutshell-core/users/users-types';

import type {ControlOption} from 'shells/card/segmented-control';
import {CalendarSquareRegularActivityIcon} from 'shells/icon/svg/calendar-square-regular-activity-icon';
import {EnvelopeNewIcon} from 'shells/icon/svg/envelope-new-icon';
import {MobilePhoneRegularIcon} from 'shells/icon/svg/mobile-phone-regular-icon';
import {NoteRegularIcon} from 'shells/icon/svg/note-regular-icon';
import {PhoneOutlineIcon} from 'shells/icon/svg/phone-outline-icon';
import {TextableSelectPopover} from 'shells/inbox/textable-select/textable-select-popover';
import type {Textable} from 'shells/inbox/types';

import {analyticsClient} from '../../../analytics-client';
import {appHistory} from '../../../history';
import {handleCall} from '../../../phone/handle-call';
import type {NutshellState} from '../../../store';

import {useGetSessionUser} from '../../core/use-get-session-user';
import {openComposerWithId, promptOverrideMinimizedEmail} from '../../email/email-composer-actions';
import {getWasMinimizedForEntityId} from '../../email/email-composer-selectors';
import {useGetRelatedTextables} from '../../inbox/sms/graphql/hooks';
import {PhoneSettingsDialog} from '../../lead/shared/components/phone-settings-dialog';
import {SmsFeatureGateModal} from '../../limits/feature-gates/sms-feature-gate-modal';
import {SmsFeatureGateModalWithLicense} from '../../limits/feature-gates/sms-feature-gate-modal-with-license';
import {OpenableModals} from '../../openable-modals';
import * as openableModalsActions from '../../openable-modals/openable-modals-actions';

import {TAB_ACTIVITY, TAB_CALL, TAB_EMAIL, TAB_NOTE, TAB_TEXT} from '../redux/consts';
import type {TabOption} from '../redux/types';

import {NoPhoneNumbersDialog} from './no-phone-numbers-dialog';

type EntitySummary = {
    id: string;
    name: string;
};

type HandleCallClickOptions = {
    noPhoneNumbersQuestion: string;
    trackPhoneIntegrationOnboardFromLeadPage?: boolean;
};

export function useOpenEmailModal(entity: EntitySummary) {
    const dispatch = useDispatch();
    const minimizedForEntityId = useSelector((state: NutshellState) =>
        getWasMinimizedForEntityId(state)
    );

    return () => {
        if (minimizedForEntityId && minimizedForEntityId !== entity.id) {
            dispatch(promptOverrideMinimizedEmail(entity.id));
        } else {
            dispatch(openComposerWithId(entity.id, false));
        }
    };
}

export function checkPhoneIntegrationStatus(
    hasConnectedPhoneIntegration: boolean | null | undefined,
    phoneCallerType: string | null | undefined,
    onShowSettings: () => void,
    onCall: () => void,
    phoneIntegrationOnboardEvent?: AnalyticsConstant
): void {
    if (!hasConnectedPhoneIntegration && !phoneCallerType) {
        if (phoneIntegrationOnboardEvent) {
            analyticsClient.track(phoneIntegrationOnboardEvent);
        }
        appHistory.push({
            pathname: window.location.pathname,
            search: window.location.search,
            hash: '#/connect-phone-integration/get-started',
        });
    } else if (hasConnectedPhoneIntegration && !phoneCallerType) {
        onShowSettings();
    } else if (phoneCallerType) {
        onCall();
    }
}

export function useHandleCallClick(
    anchorElementId: string,
    entityId: string,
    options: HandleCallClickOptions
) {
    const [showSettingsDialog, setShowSettingsDialog] = useState(false);
    const [showTextableSelectPopover, setShowTextableSelectPopover] = useState(false);
    const [showNoPhoneNumbersDialog, setShowNoPhoneNumbersDialog] = useState(false);

    const {textables} = useGetRelatedTextables(entityId);
    const {user} = useGetSessionUser();
    const hasConnectedPhoneIntegration =
        user &&
        (user.phoneIntegrations.some(
            (integration) => integration && integration.isEnabledForUser
        ) ||
            (user.clickToCallIntegration && user.clickToCallIntegration.isEnabled));
    const phoneCallerType = user && user.phoneCallerType;

    const selectTextable = (textable: Textable) => {
        const phoneNumberFormatted = textable.phone.value.numberFormatted;
        const phoneNumberRaw = textable.phone.value.id;
        if (phoneNumberFormatted && phoneNumberRaw && phoneCallerType) {
            handleCall(
                phoneNumberFormatted,
                phoneNumberRaw,
                phoneCallerType,
                textable.entityId,
                anchorElementId
            );
        }
    };

    const onCall = () => {
        if (textables && textables.length > 1) {
            setShowTextableSelectPopover(true);
        } else if (textables && textables.length === 1 && textables[0]) {
            selectTextable(textables[0]);
        } else {
            setShowNoPhoneNumbersDialog(true);
        }
    };

    const handleCallClick = () => {
        analyticsClient.track(analyticsClient.EVENTS.TRACK_LEAD_PAGE_CALL_BUTTON_CLICKED);
        checkPhoneIntegrationStatus(
            hasConnectedPhoneIntegration,
            phoneCallerType,
            () => setShowSettingsDialog(true),
            onCall,
            options.trackPhoneIntegrationOnboardFromLeadPage
                ? analyticsClient.EVENTS.TRACK_PHONE_INTEGRATION_ONBOARD_FROM_LEAD_PAGE
                : undefined
        );
    };

    const textableSelectPopover = showTextableSelectPopover ? (
        <TextableSelectPopover
            anchor={null}
            anchorElementId={anchorElementId}
            textables={textables}
            selectedTextable={null}
            onSelectTextable={selectTextable}
            setIsPopoverOpen={setShowTextableSelectPopover}
        />
    ) : undefined;

    const settingsDialog = showSettingsDialog ? (
        <PhoneSettingsDialog
            isOpen={showSettingsDialog}
            onClose={() => setShowSettingsDialog(false)}
            anchorElementId={anchorElementId}
        />
    ) : undefined;

    const noPhoneNumbersDialog = showNoPhoneNumbersDialog ? (
        <NoPhoneNumbersDialog
            isOpen={showNoPhoneNumbersDialog}
            onClose={() => setShowNoPhoneNumbersDialog(false)}
            anchorElementId={anchorElementId}
            question={options.noPhoneNumbersQuestion}
        />
    ) : undefined;

    return {
        handleCallClick,
        popoverComponent: textableSelectPopover || settingsDialog || noPhoneNumbersDialog,
    };
}

export function useOpenTextModal(entity: EntitySummary, user?: User | null) {
    const dispatch = useDispatch();
    const [featureGateOpen, setFeatureGateOpen] = useState(false);

    const openTextModal = () => {
        if (user && user.permissions.canUseSms) {
            dispatch(
                openableModalsActions.openModal(OpenableModals.SMS_SIDEBAR, {
                    entityId: entity.id,
                    openedFromEntityName: entity.name,
                })
            );
        } else {
            setFeatureGateOpen(true);
        }
    };

    const FeatureGateComponent = NutClientConfig.hasSms
        ? SmsFeatureGateModalWithLicense
        : SmsFeatureGateModal;

    const featureGate = (
        <FeatureGateComponent
            isOpen={featureGateOpen}
            onClose={() => {
                setFeatureGateOpen(false);
            }}
        />
    );

    return {
        openTextModal,
        featureGate,
    };
}

export const getTabOptions = (
    openEmailModal: () => unknown,
    openTextModal: () => unknown,
    handleCallClick: () => unknown
): ControlOption<TabOption>[] => [
    {
        type: 'small',
        title: 'Log activity',
        value: TAB_ACTIVITY,
        icon: CalendarSquareRegularActivityIcon,
    },
    {
        type: 'small',
        title: 'Write note',
        value: TAB_NOTE,
        icon: NoteRegularIcon,
    },
    {
        type: 'small',
        title: 'Send email',
        value: TAB_EMAIL,
        justAButton: true,
        onClick: openEmailModal,
        icon: EnvelopeNewIcon,
    },
    {
        type: 'small',
        title: 'Send text',
        value: TAB_TEXT,
        justAButton: true,
        onClick: openTextModal,
        icon: MobilePhoneRegularIcon,
    },
    {
        type: 'small',
        title: 'Call contact',
        value: TAB_CALL,
        justAButton: true,
        onClick: handleCallClick,
        icon: PhoneOutlineIcon,
        id: TAB_CALL,
    },
];
